import { CustomCard } from '@/components/custom-card';
import { SubheadingSm } from '@/components/custom-titles';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import { getBarValueLabelX } from '@/utils/chart';
import { formatNumber } from '@/utils/numberFormat';
import { CHART_ROW_COLORS } from '../constants';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts';

export interface RankedBarChartItem {
  name: string;
  value: number;
}

interface RankedBarChartProps {
  title: string;
  data: RankedBarChartItem[];
  isLoading?: boolean;
  className?: string;
  /** Nechta qator ko'rsatilsin. Berilmasa — barchasi. */
  limit?: number;
  /** Qiymat yonidagi birlik, masalan "ta". */
  valueSuffix?: string;
  /** `true` — nom chiziq ichida (keng chiziqlar), `false` — chapdagi ustunda. */
  labelInside?: boolean;
  /** Qatorlar palitrasi — yuqoridan pastga. */
  colors?: string[];
}

/** Bitta qator balandligi — diagramma qatorlar soniga qarab o'sadi. */
const ROW_HEIGHT = 34;

/** Reyting ko'rinishidagi gorizontal diagramma: qiymat bo'yicha kamayish
 *  tartibida saralanadi. Ikkala uslub ham shu komponentdan chiqadi. */
export const RankedBarChart = ({
  title,
  data,
  isLoading,
  className,
  limit,
  valueSuffix,
  labelInside,
  colors = CHART_ROW_COLORS.violet,
}: RankedBarChartProps) => {
  const { t } = useTranslation();

  const chartData = data
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, limit ?? undefined);

  const formatValue = (value: number) =>
    valueSuffix ? `${formatNumber(value)} ${valueSuffix}` : formatNumber(value);

  return (
    <CustomCard className={cn('flex w-full flex-col gap-4', className)}>
      <SubheadingSm title={title} />

      {isLoading ? (
        <div className="flex h-[300px] items-center justify-center">
          <div className="progress" />
        </div>
      ) : chartData.length ? (
        <div style={{ height: chartData.length * ROW_HEIGHT + 16 }}>
          <ChartContainer
            config={{ value: { label: ' ', color: colors[0] } }}
            className="h-full w-full"
          >
            <BarChart
              data={chartData}
              layout="vertical"
              barSize={labelInside ? 24 : 14}
              /* Chetlarda ortiqcha bo'sh joy qolmasin: o'ngda faqat qiymat
                 yorlig'iga yetadigan joy. */
              margin={{
                left: 0,
                right: valueSuffix ? 34 : 80,
                top: 4,
                bottom: 4,
              }}
            >
              <YAxis
                dataKey="name"
                type="category"
                width={labelInside ? 0 : 150}
                tickLine={false}
                axisLine={false}
                tick={
                  labelInside
                    ? false
                    : { fill: 'rgba(255,255,255,0.7)', fontSize: 12 }
                }
              />
              <XAxis type="number" hide />

              <ChartTooltip
                cursor={false}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0].payload as RankedBarChartItem;
                  return (
                    <div className="rounded-md bg-foreground px-3 py-2 text-sm shadow">
                      <span className="font-medium text-[#101014]">
                        {row.name}
                      </span>
                      <span className="ml-2 text-[#101014]">
                        : {formatValue(row.value)}
                      </span>
                    </div>
                  );
                }}
              />

              <Bar dataKey="value" radius={4}>
                {chartData.map((item, index) => (
                  <Cell
                    key={item.name}
                    fill={colors[Math.min(index, colors.length - 1)]}
                  />
                ))}

                {labelInside && (
                  <LabelList
                    dataKey="name"
                    position="insideLeft"
                    offset={12}
                    fill="#fff"
                    fillOpacity={1}
                    fontSize={12}
                  />
                )}
                {/* Chiziq qisqa bo'lsa nom undan oshib ketadi — qiymat
                    o'shanda nomdan keyin chiqadi, aks holda chiziqdan keyin. */}
                <LabelList
                  dataKey="value"
                  content={({ x, y, width, height, value, index }) => (
                    <text
                      x={
                        labelInside
                          ? getBarValueLabelX({
                              barX: Number(x),
                              barWidth: Number(width),
                              name: chartData[Number(index)]?.name || '',
                              insideOffset: 12,
                            })
                          : Number(x) + Number(width) + 8
                      }
                      y={Number(y) + Number(height) / 2}
                      dy={4}
                      fill="#fff"
                      fontSize={12}
                      fontWeight={500}
                    >
                      {formatValue(Number(value))}
                    </text>
                  )}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      ) : (
        <div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
          {t('no_data')}
        </div>
      )}
    </CustomCard>
  );
};
